import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../common/Sidebar';
import AudienceSelection from '../common/AudienceSelection';
import apiClient from '../../services/apiClient';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../services/BusinessContext';
import '../../styles/fonts.css';
import styles from './BusinessAdminDashboard.module.css';

const cx = (...classes) => classes.filter(Boolean).join(' ');

const BusinessAdminDashboard = () => {
    const navigate = useNavigate();
    const { 
        business, 
        loading, 
        error, 
        aiPoints, 
        tier, 
        permissions, 
        totalAdminSeats,
        usedAdminSeats,
        refreshBusiness, 
        setBusiness 
    } = useBusiness();
    const [isLoading, setIsLoading] = useState(true);
    
    // Tooltip texts for help icons
    const tooltipTexts = {
        "New Survey": "Build a custom survey from scratch with as many questions as you like.",
        "AI Survey Builder": "Let AI create your survey from a simple prompt—fast, smart, and easy.",
    "Audience Selection": "Choose who sees your brand page—open to all or limited to specific groups like Discord roles or email domains.",
        "Business Analytics": "View results and insights from your survey in a clear, easy-to-read report.",
        "Manage Surveys": "Manage your surveys—publish or unpublish, duplicate them, choose who can respond, delete if needed, and view analytics."
    };
    
    // Local copy of user that we can update
    const [localUser, setLocalUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));

    // Business audience modal states
    const [showBusinessAudienceModal, setShowBusinessAudienceModal] = useState(false);

    // Tooltip management - append to body to escape stacking contexts
    useEffect(() => {
        let tooltipElement = null;
        let tooltipArrow = null;
        let currentIcon = null;
        let cleanupFunctions = [];

        const createTooltip = (icon, text) => {
            // Remove any existing tooltip
            if (tooltipElement) {
                tooltipElement.remove();
            }
            if (tooltipArrow) {
                tooltipArrow.remove();
            }

            // Create tooltip container
            tooltipElement = document.createElement('div');
            tooltipElement.className = 'dynamic-tooltip';
            tooltipElement.textContent = text;
            tooltipElement.style.cssText = `
                position: fixed;
                background: #ffffff;
                color: #aa2eff;
                border: 1px solid #aa2eff;
                border-radius: 6px;
                padding: 8px 12px;
                min-width: 200px;
                max-width: 400px;
                text-align: left;
                word-wrap: break-word;
                white-space: normal;
                font-size: 12px;
                line-height: 1.4;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                z-index: 999999;
                pointer-events: none;
                opacity: 0;
                transition: opacity 0.15s ease;
                visibility: hidden;
            `;

            // Create arrow
            tooltipArrow = document.createElement('div');
            tooltipArrow.className = 'dynamic-tooltip-arrow';
            tooltipArrow.style.cssText = `
                position: fixed;
                width: 8px;
                height: 8px;
                background: #ffffff;
                border-top: 1px solid #aa2eff;
                border-left: 1px solid #aa2eff;
                transform: rotate(45deg);
                z-index: 1000000;
                pointer-events: none;
                opacity: 0;
                transition: opacity 0.15s ease;
                visibility: hidden;
            `;

            // Append to body as last children to ensure they're painted on top
            // Remove any existing tooltips first
            const existingTooltips = document.querySelectorAll('.dynamic-tooltip, .dynamic-tooltip-arrow');
            existingTooltips.forEach(el => el.remove());
            
            // Append to body - being appended last in DOM order ensures it's painted on top
            document.body.appendChild(tooltipElement);
            document.body.appendChild(tooltipArrow);

            // Add mouseleave handler to tooltip
            tooltipElement.addEventListener('mouseleave', removeTooltip);

            // Position tooltip
            const positionTooltip = () => {
                if (!icon || !tooltipElement || !tooltipArrow) return;

                const iconRect = icon.getBoundingClientRect();
                const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
                const scrollY = window.pageYOffset || document.documentElement.scrollTop;

                // Calculate tooltip position (below icon, centered)
                const tooltipTop = iconRect.bottom + scrollY + 8;
                const tooltipLeft = iconRect.left + scrollX + (iconRect.width / 2);

                // Position tooltip
                tooltipElement.style.left = `${tooltipLeft}px`;
                tooltipElement.style.top = `${tooltipTop}px`;
                tooltipElement.style.transform = 'translateX(-50%)';

                // Position arrow (above tooltip)
                const arrowTop = iconRect.bottom + scrollY + 2;
                tooltipArrow.style.left = `${tooltipLeft}px`;
                tooltipArrow.style.top = `${arrowTop}px`;
                tooltipArrow.style.transform = 'translateX(-50%) rotate(45deg)';

                // Show tooltip
                tooltipElement.style.opacity = '1';
                tooltipElement.style.visibility = 'visible';
                tooltipArrow.style.opacity = '1';
                tooltipArrow.style.visibility = 'visible';
            };

            // Position and show
            setTimeout(() => {
                positionTooltip();
            }, 0);

            // Update position on scroll/resize
            const updatePosition = () => positionTooltip();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);

            cleanupFunctions.push(() => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            });
        };

        const removeTooltip = () => {
            if (tooltipElement) {
                tooltipElement.style.opacity = '0';
                tooltipElement.style.visibility = 'hidden';
                setTimeout(() => {
                    if (tooltipElement) {
                        tooltipElement.remove();
                        tooltipElement = null;
                    }
                }, 150);
            }
            if (tooltipArrow) {
                tooltipArrow.style.opacity = '0';
                tooltipArrow.style.visibility = 'hidden';
                setTimeout(() => {
                    if (tooltipArrow) {
                        tooltipArrow.remove();
                        tooltipArrow = null;
                    }
                }, 150);
            }
            currentIcon = null;
        };

        const handleMouseEnter = (e) => {
            // Ignore if hovering over tooltip itself
            if (e.target.closest('.dynamic-tooltip') || e.target.closest('.dynamic-tooltip-arrow')) {
                return;
            }

            const icon = e.target.closest('.info-icon');
            if (!icon || icon === currentIcon) return;

            const tooltipText = icon.getAttribute('data-tooltip');
            if (!tooltipText) return;

            currentIcon = icon;
            createTooltip(icon, tooltipText);
        };

        const handleMouseLeave = (e) => {
            // Don't remove if moving to tooltip
            const relatedTarget = e.relatedTarget;
            if (relatedTarget && (relatedTarget.closest('.dynamic-tooltip') || relatedTarget.closest('.dynamic-tooltip-arrow'))) {
                return;
            }

            const icon = e.target.closest('.info-icon');
            if (icon && icon === currentIcon) {
                removeTooltip();
            }
        };

        // Add event listeners using event delegation
        document.addEventListener('mouseover', handleMouseEnter, true);
        document.addEventListener('mouseout', handleMouseLeave, true);

        return () => {
            document.removeEventListener('mouseover', handleMouseEnter, true);
            document.removeEventListener('mouseout', handleMouseLeave, true);
            
            // Clean up tooltips
            if (tooltipElement) {
                tooltipElement.remove();
            }
            if (tooltipArrow) {
                tooltipArrow.remove();
            }
            
            // Clean up scroll/resize listeners
            cleanupFunctions.forEach(cleanup => cleanup());
        };
    }, []);
    const [businessAudienceSettings, setBusinessAudienceSettings] = useState(null);
    const [audienceLoading, setAudienceLoading] = useState(false);
    const [audienceError, setAudienceError] = useState(null);
    

    // Get business admin's business info from localStorage
    const businessId = localUser.business_id;
    const adminPermissions = localUser.business_admin_permissions || {};



    useEffect(() => {
        // Business context is handled by BusinessProvider
        setIsLoading(loading);
        
        if (error) {
            toast.error(error);
        }
        
        if (!localUser.business_id && !loading) {
            toast.error('Business Admin not associated with a business.');
            navigate('/login');
        }
    }, [loading, error, localUser.business_id, navigate]);

    const handleNavigation = (path, actionType = "") => {
        if (!business) return;

        if (path) {
            navigate(path, { state: { businessId: business.id, businessName: business.name } });
        } else {
            console.warn(`Navigation path for ${actionType} is undefined.`);
            toast(`Feature '${actionType}' for ${business.name} is coming soon!`, {
                icon: "ℹ️",
            });
        }
    };

    const handleManageBusinessAudience = async () => {
        setAudienceLoading(true);
        setAudienceError(null);
        
        try {
            const response = await apiClient.get(`/api/businesses/${businessId}/audience`);
            setBusinessAudienceSettings(response.data);
        } catch (error) {
            console.error('Error loading business audience settings:', error);
            setAudienceError(error.response?.data?.error || 'Failed to load audience settings');
            setBusinessAudienceSettings({});
        } finally {
            setAudienceLoading(false);
        }
        
        setShowBusinessAudienceModal(true);
    };

    const handleSaveBusinessAudience = async (settings) => {
        setAudienceLoading(true);
        setAudienceError(null);
        
        try {
            await apiClient.put(`/api/businesses/${businessId}/audience`, settings);
            toast.success('Business audience settings updated successfully!');
            setShowBusinessAudienceModal(false);
            const response = await apiClient.get(`/api/businesses/${businessId}`);
            setBusiness(response.data);
        } catch (error) {
            console.error('Error updating business audience settings:', error);
            setAudienceError(error.response?.data?.error || 'Failed to update audience settings');
        } finally {
            setAudienceLoading(false);
        }
    };


    const businessActionGroups = business ? [
        {
            groupTitle: "Surveys",
            items: [
                { label: "New Survey", path: `/admin/business/${businessId}/surveys/new`, actionKey: "can_create_surveys" },
                { label: "AI Survey Builder", path: '/survey-builder', actionKey: "can_create_surveys", requiresAI: true },
                { label: "Manage Surveys", path: `/admin/business/${businessId}/surveys/manage`, actionKey: "can_edit_surveys" },
            ]
        }
    ] : [];

    if (isLoading) {
        return (
            <div className={cx('page-container', styles['page-container'])}>
                <Sidebar />
                <div className={cx('main-content', styles['main-content'], styles['business-specific-dashboard'])}>
                    <p>Loading business dashboard...</p>
                </div>
            </div>
        );
    }

    if (!business) {
        return (
            <div className={styles['page-container']}>
                <Sidebar />
                <div className={(styles['main-content'], styles['business-specific-dashboard'])}>
                    <p>Business not found or access denied.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={(styles['page-container'])}>
            <Sidebar />
            <div className={cx('main-content', 'business-specific-dashboard', styles['main-content'], styles['business-specific-dashboard'])}>
                <div className={cx('dashboard-header', styles['dashboard-header'])}>
                    <h1 className={cx('dashboard-title', styles['dashboard-title'])} style={{ color: 'black' }}>Business Admin Panel</h1>
                    <p className={cx('dashboard-subtitle', styles['dashboard-subtitle'])} style={{ color: 'black' }}>
                        Managing: {business.name} | Tier: {tier} | Status: {business.is_active ? "Active" : "Inactive"}
                    </p>
                    <div className={cx('ai-points-display', styles['ai-points-display'])}>
                        <div className={cx('points-balance', styles['points-balance'])}>
                                <i className="ri-cpu-line"></i>
                            <span>AI Points: <strong>{aiPoints}</strong></span>
                            {aiPoints < 5 && (
                                <button 
                                    className={cx('buy-points-btn', styles['buy-points-btn'])}
                                    onClick={() => navigate('/business/purchase-points')}
                                >
                                    Buy More Points
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className={cx('dashboard-grid', styles['dashboard-grid'])}>
                    {businessActionGroups.map((group, groupIndex) => {
                        const permittedItems = group.items.filter(item => {
                            if (!item.actionKey) return true;
                            return permissions[item.actionKey] === true && adminPermissions[item.actionKey] === true;
                        });

                        return permittedItems.length > 0 && (
                            <div className={cx('dashboard-card', styles['dashboard-card'])} key={groupIndex}>
                                <h2 className={cx('card-title', styles['card-title'])}>
                                    {group.groupTitle}
                                    {tooltipTexts[group.groupTitle] && (
                                        <i
                                            className={`ri-information-line info-icon`}
                                            data-tooltip={tooltipTexts[group.groupTitle]}
                                            style={{ marginLeft: '4px' }}
                                        ></i>
                                    )}
                                </h2>
                                <div className={cx('card-actions', styles['card-actions'])}>
                                    {permittedItems.map((action, actionIndex) => (
                                        <button
                                            key={actionIndex}
                                            className={cx('dashboard-button', styles['dashboard-button'])}
                                            onClick={() => {
                                                if (action.action === 'manage_audience') {
                                                    handleManageBusinessAudience();
                                                } else {
                                                    handleNavigation(action.path, action.label);
                                                }
                                            }}
                                        >
                                            <i className={action.icon}></i>
                                            <span>
                                                {action.label}
                                                {tooltipTexts[action.label] && (
                                                    <i
                                                        className={`ri-information-line info-icon`}
                                                        data-tooltip={tooltipTexts[action.label]}
                                                        style={{ marginLeft: '4px' }}
                                                    ></i>
                                                )}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )
                    })}
                    
                    {businessActionGroups.every(group =>
                        group.items.filter(item => {
                            if (!item.actionKey) return true;
                            return permissions[item.actionKey] === true && adminPermissions[item.actionKey] === true;
                        }).length === 0
                    ) && (
                        <div className={cx('dashboard-card', styles['dashboard-card'])}>
                            <h2 className={cx('card-title', styles['card-title'])}>No Actions Available</h2>
                            <p>You do not have permissions for any actions. Contact a super admin.</p>
                        </div>
                    )}
                </div>

                {/* Business Audience Selection Modal */}
                {showBusinessAudienceModal && (
                    <div className={cx('modal-backdrop', styles['modal-backdrop'])} onClick={() => setShowBusinessAudienceModal(false)}>
                        <div className={cx('modal-content', styles['modal-content'])} onClick={(e) => e.stopPropagation()}>
                            <div className={cx('modal-header', styles['modal-header'])}>
                                <h2 className={cx('chat-title', styles['chat-title'])}>
                                    <i className="ri-group-line"></i>
                                    Audience Selection
                                </h2>
                                <button 
                                    className={cx('close-button', styles['close-button'])} 
                                    onClick={() => setShowBusinessAudienceModal(false)}
                                    aria-label="Close"
                                >
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div style={{ padding: '20px', maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' }}>
                                {business && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>
                                            {business.name}
                                        </h3>
                                        <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                                            Configure who can access your business and its content. 
                                            {business.audience_type === 'PUBLIC' ? 
                                                ' Currently set to PUBLIC - anyone can view your business.' :
                                                ' Currently set to RESTRICTED - only specified users can access.'
                                            }
                                        </p>
                                    </div>
                                )}
                                
                                <AudienceSelection
                                    type="business"
                                    initialSettings={businessAudienceSettings || {}}
                                    onSave={handleSaveBusinessAudience}
                                    loading={audienceLoading}
                                    error={audienceError}
                                />
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default BusinessAdminDashboard; 
